import React, { useState } from "react";

import { DateTime } from "luxon";

import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";

import {
  HeaderTimeText,
  VerticalDivider,
} from "@app/analytics/components/reports/styles";
import { EditIcon } from "@app/common/assets/icons";
import { formatStartAndEndTime } from "@app/common/utils";

import ConfirmationModal from "@rui/ConfirmationModal";
import DatePicker from "@rui/DatePicker/DatePicker";
import Stack, { Row } from "@rui/Stack";
import TimePicker from "@rui/TimePicker/TimePicker";

interface BaseDateAndTimeProps {
  startTime: DateTime;
  endTime?: DateTime;
}

interface EditableDateAndTimeProps extends BaseDateAndTimeProps {
  onEdit: (opts: { startTime: DateTime }) => void;
}

interface DateAndTimeWrapperProps extends EditableDateAndTimeProps {
  editable: boolean;
}

const DateAndTimeRow = styled(Row)(({ theme }) => ({
  padding: "0 2px",
  borderRadius: theme.corners.small,
  transition: "all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
  "& .meeting-report__date-and-time__edit": {
    fontSize: "12px",
    [theme.breakpoints.up("mobile")]: {
      fontSize: "14px",
    },
    display: "none",
    color: theme.palette.shades.primary[500],
  },
  "&:hover": {
    backgroundColor: theme.palette.shades.ghost[400],
    cursor: "pointer",
    "& .meeting-report__date-and-time__edit": {
      display: "inline",
    },
  },
}));

const ReadonlyDateAndTime = ({ startTime, endTime }: BaseDateAndTimeProps) => (
  <>
    <HeaderTimeText>
      {startTime.toLocaleString({
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      })}
    </HeaderTimeText>
    <VerticalDivider size="sm" />
    <HeaderTimeText>{formatStartAndEndTime(startTime, endTime)}</HeaderTimeText>
  </>
);

const EditableDateAndTime = ({
  startTime,
  endTime,
  onEdit,
}: EditableDateAndTimeProps) => {
  const [editDateModalOpen, setEditDateModalOpen] = useState(false);
  const [start, setStart] = useState(startTime);
  const [date, setDate] = useState(startTime);

  const handleSaveEdits = () => {
    const mergedStartTime = date?.set({
      hour: start?.hour,
      minute: start?.minute,
    });

    onEdit({ startTime: mergedStartTime });
    setEditDateModalOpen(false);
  };

  return (
    <>
      <DateAndTimeRow
        variant="left"
        spacing={1}
        onClick={() => setEditDateModalOpen(true)}
      >
        <ReadonlyDateAndTime startTime={startTime} endTime={endTime} />
        <EditIcon className="meeting-report__date-and-time__edit" />
      </DateAndTimeRow>
      <ConfirmationModal
        open={editDateModalOpen}
        title="Edit date & time"
        onClose={() => setEditDateModalOpen(false)}
        onConfirm={handleSaveEdits}
        onCancel={() => setEditDateModalOpen(false)}
        confirmProps={{ label: "Save changes" }}
      >
        <Stack spacing={2}>
          <Stack variant="left" spacing={0.5}>
            <Typography variant="labelReg">Date</Typography>
            <DatePicker
              disableFuture
              value={date}
              onChange={(newValue) => setDate(newValue ?? startTime)}
            />
          </Stack>
          <Stack variant="left" spacing={0.5}>
            <Typography variant="labelReg">Time</Typography>
            <TimePicker
              value={start}
              onChange={(newValue) => setStart(newValue ?? startTime)}
            />
          </Stack>
        </Stack>
      </ConfirmationModal>
    </>
  );
};

const DateAndTime = ({ editable, ...props }: DateAndTimeWrapperProps) => {
  if (editable) {
    return <EditableDateAndTime {...props} />;
  }

  return <ReadonlyDateAndTime {...props} />;
};

export default DateAndTime;
